import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  usePropertyName,
  usePropertyPhone,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../vibesites-pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance, getContrastTextColor } from '../utils/luminance';
import { resolveHref, rewriteLegacyLinks } from '../utils/resolveHref';
import { useLocalizedSpelling } from '../hooks/useLocalizedSpelling';

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation.
    Home is omitted because the logo already links to `/`. */
const NAV_EXCLUDED_ALIASES = new Set([
  'home',
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'schedule-a-tour',
  'hyperlocal',
  'reviews',
]);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface PageSection {
  slot: string;
}

/** `HeroSolidBG` plus the per-page heroes that render through `HeroSolidBGView`. */
const SOLID_HERO_SLOTS = new Set([
  'HeroSolidBG',
  'AmenitiesHero',
  'FloorPlansHero',
  'GalleryHero',
  'NeighborhoodHero',
  'ContactHero',
  'VirtualTourHero',
]);

/**
 * Returns true when the page identified by `alias` (or, as a fallback, by
 * `pathname`) has `HeroSolidBG` as its primary hero — i.e. an interior page
 * where the navigation overlays the solid-primary hero rather than a
 * photographic hero. Looking up by alias works during SSG (the runtime
 * passes `page.alias` through `usePage()`) so the resulting class is baked
 * into the static HTML and the nav text is correct on first paint.
 */
function isOverSolidHero(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData as Record<string, PageEntry>;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return !!hero && SOLID_HERO_SLOTS.has(hero.slot);
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 */
function buildNavLinks(hasVirtualTours: boolean, localize: (text: string) => string): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: localize(page.name), href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    // External apply URL — open in a new tab by default.
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    // External resident portal — open in a new tab by default.
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultValue: '280 Main St, Lucan, ON N0M 2J0, Canada',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Office hours (mobile drawer) — mirrors the Footer's office-hours column so
// the drawer's "Office Hours" block reads from the same `office_hours`
// collection and groups consecutive same-hours days into ranges.
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the incoming entries by normalized day. We iterate the canonical
  // week below rather than the raw list so that any day NOT pulling hours
  // through (missing entirely, or present but with no open/close time) is
  // still rendered — marked "Closed" — instead of silently dropped.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) {
      byDay.set(key, entry);
    }
  }

  const groups: GroupedHours[] = [];
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed =
      !entry ||
      (!hasValue(entry.open_time) && !hasValue(entry.close_time)) ||
      /closed/i.test(entry.open_time || '') ||
      /closed/i.test(entry.close_time || '');
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} – ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.shortLabel.indexOf(' – ');
      const startShort = dash !== -1 ? prev.shortLabel.slice(0, dash) : prev.shortLabel;
      prev.shortLabel = `${startShort} – ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }
  return groups;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

/** Normalize a pathname for active-link comparison: strip a trailing slash
    on everything but the root so "/amenities" and "/amenities/" match. */
function normalizePath(p?: string | null): string {
  if (!p) return '/';
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

/** Brief grace period before a hover menu closes so the pointer can cross
    from the trigger into the panel without the menu vanishing. */
const NAV_DROPDOWN_CLOSE_MS = 180;

function useHoverMenu() {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, NAV_DROPDOWN_CLOSE_MS);
  }, [clearCloseTimer]);

  const dismissMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return { open, openMenu, closeMenu, dismissMenu };
}

function DesktopNavLink({ link, active = false }: { link: NavLink; active?: boolean }) {
  const hasChildren = !!(link.children && link.children.length);
  const { open, openMenu, closeMenu, dismissMenu } = useHoverMenu();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        dismissMenu();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') dismissMenu();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, dismissMenu]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap${active ? ' nav-active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
    >
      {/* The parent label is still a real link so clicking it navigates to
          the parent page; the chevron + dropdown surface the child pages on
          hover/focus without stealing the primary affordance. */}
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap${active ? ' nav-active' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={openMenu}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      {/* pt-1.5 (not mt-*) keeps a hoverable bridge between the trigger and
          the panel so the pointer never leaves the group while moving down. */}
      <div
        className={`absolute left-0 top-full pt-1.5 z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1 pointer-events-none'
        }`}
      >
        <div
          className="nav-more-dropdown border border-[var(--vibe-color-border)] shadow-lg min-w-[200px]"
          style={{ backgroundColor: 'var(--vibe-color-background)' }}
        >
          {link.children!.map((child) => (
            <a
              key={child.href}
              href={child.href}
              className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks, currentPath }: { navLinks: NavLink[]; currentPath?: string }) {
  const current = normalizePath(currentPath);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const { open: moreOpen, openMenu: openMore, closeMenu: closeMore, dismissMenu: dismissMore } =
    useHoverMenu();
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        dismissMore();
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen, dismissMore]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="header-measure-row flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-semibold tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-start gap-5 xl:gap-7 flex-1 min-w-0 ml-8 xl:ml-12"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink
            key={link.href}
            link={link}
            active={
              normalizePath(link.href) === current ||
              !!link.children?.some((c) => normalizePath(c.href) === current)
            }
          />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={openMore}
            onMouseLeave={closeMore}
          >
            <button
              onClick={() => (moreOpen ? dismissMore() : openMore())}
              onFocus={openMore}
              className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* pt-1.5 bridge matches DesktopNavLink — keeps hover continuous. */}
            <div
              className={`absolute right-0 top-full pt-1.5 z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1 pointer-events-none'
              }`}
            >
              <div
                className="nav-more-dropdown border border-[var(--vibe-color-border)] shadow-lg min-w-[200px]"
                style={{ backgroundColor: 'var(--vibe-color-background)' }}
              >
                {overflowLinks.map((link) => (
                  <React.Fragment key={link.href}>
                    <a
                      href={link.href}
                      className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
                    >
                      {link.label}
                    </a>
                    {link.children?.map((child) => (
                      <a
                        key={child.href}
                        href={child.href}
                        // Indent child rows so the More dropdown reads as a
                        // nested list when a parent (e.g. Floor Plans) gets
                        // pushed into overflow and we want its sub-items to
                        // remain reachable from the dropdown.
                        className="rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text-muted)] transition-colors"
                      >
                        {child.label}
                      </a>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// Typeface used for the mobile drawer's nav links and section headings so
// they match the FloorplansList feature title (Oswald, loaded globally by the
// Google Fonts effect below; Arial Narrow / system-ui as fallbacks).
const DRAWER_HEADING_FONT = 'var(--vibe-font-heading)';

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  const overSolidHero = useMemo(
    () =>
      isOverSolidHero(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );

  // Pick the more readable text color for the transparent nav when it sits on
  // top of `HeroSolidBG` (which paints the section in `--vibe-color-primary`).
  // Uses the same `getContrastTextColor` helper the Footer uses (and that the
  // tokens runtime uses to compute `--vibe-color-text-on-primary`), so the
  // nav, the hero CTA, and the footer all pick the same contrast side on a
  // given primary.
  //
  // The earlier implementation here did `pickHighestContrast(primary,
  // [textOnPrimary, textOnSecondary])`, which was technically WCAG-optimal
  // but produced black text on medium-tone primaries (gold, tan, dusty
  // blue): on a primary with luminance ~0.3, dark-on-medium scores ~5:1 vs.
  // white-on-medium ~3:1, so the picker chose dark — disagreeing with the
  // runtime's own text-on-primary token and rendering muddy black nav links
  // on what should be a white-text design. The shared helper applies the
  // documented 0.55 threshold that favors white on those medium tones.
  const tokens = useTokens();
  const navOverHeroColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const localize = useLocalizedSpelling();
  const navLinks = useMemo(() => buildNavLinks(hasVirtualTours, localize), [hasVirtualTours, localize]);

  // Inherited parent-theme sections render their own CTA hrefs, so legacy
  // paths like "/tour" in their saved content are rewritten here site-wide.
  useEffect(() => {
    rewriteLegacyLinks();
    const observer = new MutationObserver(() => rewriteLegacyLinks());
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    return () => observer.disconnect();
  }, []);

  // Content tokens — prefer the editable token (defaults to call tracking),
  // then usePropertyPhone() which falls back to property_phone.
  const phoneToken = useContentToken('header.phone');
  const trackedPhone = usePropertyPhone();
  const phone = phoneToken || trackedPhone || '(555) 555-5555';

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const color4 = usePropertyField('property_color_4')?.trim();
  const navLineColor = color4 || 'var(--vibe-color-secondary)';
  const utilRowStyle = useMemo(() => {
    if (!color4) return undefined;
    return {
      ['--header-util-bg' as string]: color4,
      ['--header-util-text' as string]: getContrastTextColor(color4),
    } as React.CSSProperties;
  }, [color4]);

  // Logo color choice:
  //   • Scrolled (solid white --vibe-color-background) → primary logo from
  //     the header.logo slot (defaults to property_primary_logo).
  //   • Transparent over HeroSolidBG (interior page) → match the picked
  //     on-color token: light text → mono_white, dark text → mono_black.
  //   • Transparent over photographic Hero (home) → mono_white (reads
  //     against the dark gradient overlay).
  const wantsDarkOnHero = !!navOverHeroColor && relativeLuminance(navOverHeroColor) < 0.5;
  const logoImage = scrolled
    ? logoRaw.image
    : overSolidHero && wantsDarkOnHero
      ? (monoBlackLogo || logoRaw.image)
      : (monoWhiteLogo || logoRaw.image);
  const logo = { ...logoRaw, image: logoImage };
  // The parent runtime types the logo slot group's resolved value as only
  // `{ image }`, so read the `alt` field (declared on logoSlotDef) through a
  // narrowed cast for both the header-bar and drawer logos.
  const logoAlt = (logoRaw as { alt?: string }).alt;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Property name — fallback wordmark in the drawer when no white logo exists.
  const propertyName = usePropertyName();

  // Office hours for the mobile drawer, read from the same `office_hours`
  // collection the Footer uses and grouped into day ranges.
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Current page path for active-link styling in the drawer.
  const currentPath = normalizePath(
    pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
  );

  // White logo for the dark drawer (independent of the header bar's
  // scroll / over-hero logo swap above).
  const mobileLogoImage = monoWhiteLogo || logoRaw.image;

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&family=Oswald:wght@400;500;600&family=Space+Mono:wght@400;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out${scrolled ? ' header-scrolled' : ' bg-transparent header-transparent'}${overSolidHero ? ' header-over-solid-hero' : ''}`}
        style={{
          top: 0,
          transition: 'background-color 0.5s ease',
          backgroundColor: scrolled ? 'var(--vibe-color-background)' : undefined,
          // Luminance-picked text color for the transparent nav over a
          // `HeroSolidBG` (primary-color) section. Falls back to white via the
          // CSS rules below when this var is absent (e.g. home page with the
          // photographic hero, or before tokens have hydrated).
          ...(navOverHeroColor ? { ['--rise-nav-over-hero-text' as string]: navOverHeroColor } : {}),
        }}
      >
        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Sits between the SpecialOfferBanner
              (which adds its height to the header-bar's paddingTop above)
              and the main nav row below. Hosts the phone, Apply Now, and
              Resident Access links, right-aligned. The links remain
              available on mobile via the drawer further down — we don't
              surface a second row on small screens because vertical space
              matters more there. `header-util-row` paints a solid
              --vibe-color-primary (brand) strip so it reads as a distinct
              bar above the transparent main nav (see the <style> block). */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block" style={utilRowStyle}>
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Right-aligned utility links on the solid primary bar:
                  phone, Apply Now, Resident Access. `justify-end` pushes the
                  group to the right edge to mirror the design. The address
                  (and its `header.address_link` slot group) lives only in the
                  mobile drawer below now. */}
              <div className="flex items-center justify-end gap-6 h-9">
                {/* Phone */}
                <a
                  href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                  className="dniphone dniphonehref header-util-link"
                  aria-label="Call us"
                >
                  <EditableToken
                    path="header.phone"
                    as="span"
                  >
                    {formatPhone(phone)}
                  </EditableToken>
                </a>

                {/* Apply Now — property_apply_now_url (external), not /apply-now */}
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={resolveHref(applyNowLink.url || '#')}
                    target={applyNowLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="header-util-link"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                {/* Resident Access — property_resident_portal_url, not /resident-access */}
                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={resolveHref(residentAccessLink.url || '#')}
                    target={residentAccessLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="header-util-link"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-24 lg:h-28">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                {/* Fixed logo box so the transparent (mono) and scrolled
                    (primary) assets share the same display size — `h-auto`
                    let each image resolve to a different height from its
                    own aspect ratio against the max caps. */}
                <a
                  href="/"
                  className="flex-shrink-0 flex items-center h-14 lg:h-16 w-[170px] sm:w-[190px] lg:w-[200px]"
                >
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logoAlt || 'Community Logo'}
                      sizes="200px"
                      className="h-full w-full object-contain object-left"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Rise
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links — left-aligned next to the logo.
                  Active-link detection prefers the runtime-resolved page path
                  (usePage) over window.location: in the editor/preview the
                  iframe URL often isn't the page's real path, so the latter
                  would never match a nav href. Falls back to the live pathname
                  only when the runtime path is absent. */}
              <DesktopNav
                navLinks={navLinks}
                currentPath={pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : undefined)}
              />

              {/* Right group — mobile hamburger only. The phone / Apply Now /
                  Resident Access links live in the solid utility bar above;
                  the desktop nav row stays focused on page links. */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

      </header>

      {/* ============================================================== */}
      {/* Mobile Menu — rendered outside <header> so its z-index is not  */}
      {/* trapped in the header's z-50 stacking context. Must sit above  */}
      {/* SpecialOfferBanner (z-60). Slides in from the left over a      */}
      {/* dimmed backdrop; the page shows through the right edge.        */}
      {/* ============================================================== */}
      <div
        className={`mobile-menu-backdrop lg:hidden fixed inset-0 z-[70] bg-black/60 transition-opacity duration-300 ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <div
        className={`mobile-menu-panel lg:hidden fixed inset-y-0 left-0 z-[71] flex flex-col w-[50vw] min-w-[260px] max-w-[420px] overflow-y-auto transition-transform duration-300 ease-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
          {/* Close button + centered logo. Grid reserves a column for the X so
              wide logos cannot paint over it (absolute left + centered logo
              collided on narrow drawers). Matching right spacer keeps the logo
              optically centered. */}
          <div className="grid grid-cols-[2.25rem_1fr_2.25rem] items-center gap-3 px-5 pt-6 pb-6 flex-shrink-0">
            <button
              onClick={closeMobileMenu}
              aria-label="Close menu"
              className="w-9 h-9 flex items-center justify-center text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <a href="/" onClick={closeMobileMenu} className="flex justify-center min-w-0">
              {mobileLogoImage ? (
                <VibesitesImage
                  url={mobileLogoImage}
                  alt={logoAlt || 'Community Logo'}
                  sizes="180px"
                  className="h-auto max-h-[4.5rem] w-auto max-w-full object-contain"
                />
              ) : (
                <span className="font-heading text-2xl font-bold uppercase tracking-wide text-white truncate">
                  {propertyName || 'Our Community'}
                </span>
              )}
            </a>
            <div aria-hidden="true" />
          </div>

          {/* Navigation links — large, heading-font; white text + secondary underline */}
          <nav className="px-7 pt-2 flex flex-col items-center text-center gap-5" role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => {
              const isActive =
                normalizePath(link.href) === currentPath ||
                !!link.children?.some((c) => normalizePath(c.href) === currentPath);
              return (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    onClick={closeMobileMenu}
                    aria-current={isActive ? 'page' : undefined}
                    style={{ fontFamily: DRAWER_HEADING_FONT }}
                    className={`mobile-nav-link text-[16pt] leading-none transition-colors text-white${
                      isActive ? ' mobile-nav-link--active' : ''
                    }`}
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => {
                    const childActive = normalizePath(child.href) === currentPath;
                    return (
                      <a
                        key={child.href}
                        href={child.href}
                        onClick={closeMobileMenu}
                        aria-current={childActive ? 'page' : undefined}
                        style={{ fontFamily: DRAWER_HEADING_FONT }}
                        className={`mobile-nav-link text-[13pt] leading-none transition-colors ${
                          childActive
                            ? 'mobile-nav-link--active text-white'
                            : 'text-white/70'
                        }`}
                      >
                        {child.label}
                      </a>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </nav>

          {/* Primary CTAs — stacked, full-width outlined buttons. Wrapped in
              EditableSlotGroup so the editor can retarget / relabel them from
              inside the open drawer. */}
          <div className="px-7 pt-8 space-y-3">
            <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
              <a
                href={resolveHref(applyNowLink.url || '#')}
                target={applyNowLink._target || '_blank'}
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="mobile-drawer-btn block text-center w-full py-4 text-xs font-medium tracking-[0.2em] uppercase border border-white/60 text-white"
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
              <a
                href={resolveHref(residentAccessLink.url || '#')}
                target={residentAccessLink._target || '_blank'}
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="mobile-drawer-btn block text-center w-full py-4 text-xs font-medium tracking-[0.2em] uppercase border border-white/60 text-white"
              >
                {residentAccessLink.label || 'Resident Access'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Visit Us — address + phone */}
          <div className="px-7 pt-10 text-center">
            <h4 style={{ fontFamily: DRAWER_HEADING_FONT }} className="text-xs font-bold uppercase tracking-[0.15em] text-white mb-3">Visit Us</h4>
            <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
              <a
                href={addressLink.url || propertyAddressUrl || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="block text-sm leading-relaxed text-white/90 hover:text-[var(--vibe-color-link-hover)] transition-colors"
              >
                {addressLink.address}
              </a>
            </EditableSlotGroup>
            <a
              href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref block mt-4 text-base text-white/90 hover:text-[var(--vibe-color-link-hover)] transition-colors"
            >
              {/* EditableToken wraps just the inner text so the editor can
                  target the phone number from inside the open drawer; the
                  outer <a> still routes to `tel:` for real visitors. */}
              <EditableToken
                path="header.phone"
                as="span"
              >
                {formatPhone(phone)}
              </EditableToken>
            </a>
          </div>

          {/* Office Hours */}
          <div className="px-7 pt-8 pb-10 mt-auto text-center">
            <h4 style={{ fontFamily: DRAWER_HEADING_FONT }} className="text-xs font-bold uppercase tracking-[0.15em] text-white mb-3">Office Hours</h4>
            {groupedHours.length > 0 ? (
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm text-white/90 w-fit mx-auto">
                {groupedHours.map((group, index) => (
                  <React.Fragment key={index}>
                    <span className="whitespace-nowrap">{group.shortLabel}</span>
                    <span className="text-right whitespace-nowrap">{group.hours}</span>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <p className="text-sm text-white/50">Office hours will be displayed here.</p>
            )}
          </div>
        </div>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover,
        .header-transparent .header-bar nav a.nav-active {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header.
           Selected + hover still force white so the active page reads clearly
           against the secondary underline. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover,
        .header-transparent.header-over-solid-hero .header-bar nav a.nav-active {
          color: #fff !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. Nested subpages get a simple darker-row
           hover (not the top-level double-line border). Keep text color
           steady on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
          background-color: color-mix(in srgb, var(--vibe-color-text) 6%, var(--vibe-color-background));
        }

        /* Mobile menu panel is a solid dark drawer regardless of the header
           bar's transparent / scrolled state above it. Base text is white;
           individual links carry their own Tailwind color classes (white,
           white/90, or brand-primary when active) which win because there's
           no descendant .mobile-menu-panel a color rule competing here. */
        .mobile-menu-panel {
          background-color: #101010;
          color: rgba(255, 255, 255, 0.92);
        }

        /* Mobile drawer nav links — selected + hover stay white; underline
           uses secondary. Links are centered, so the underline grows outward
           from the middle and the label lifts slightly. Inline-block keeps
           the underline hugging the label width rather than the full row. */
        .mobile-menu-panel .mobile-nav-link {
          position: relative;
          display: inline-block;
          color: #fff;
          transition: color 0.2s ease, transform 0.2s ease;
        }
        .mobile-menu-panel .mobile-nav-link::after {
          content: '';
          position: absolute;
          left: 50%;
          bottom: -0.4rem;
          width: 0;
          height: 2px;
          background-color: ${navLineColor};
          transform: translateX(-50%);
          transition: width 0.25s ease;
        }
        .mobile-menu-panel .mobile-nav-link:hover,
        .mobile-menu-panel .mobile-nav-link.mobile-nav-link--active {
          color: #fff;
          transform: translateY(-1px);
        }
        .mobile-menu-panel .mobile-nav-link:hover::after,
        .mobile-menu-panel .mobile-nav-link.mobile-nav-link--active::after {
          width: 100%;
        }

        /* Nav links previously drew an animated underline on hover (the
           "white bottom underline"). That hover underline has been removed
           per request — links keep only the color-shift hover affordance
           from the !important rules above. The .rise-nav-darken-hover class
           is still present in markup as a styling hook but draws no
           underline; we keep the color transition so the brightening hover
           stays smooth. */
        .rise-nav-darken-hover {
          transition: color 0.2s ease;
        }
        /* Active page link — persistent brand-color rules ABOVE and BELOW
           the current page's nav item (e.g. "Home" on the home page). Drawn
           as top/bottom borders in --vibe-color-secondary so the accent reads
           against both the white scrolled header and the dark hero overlay,
           independent of the link's own text color. We use borders (rather
           than text-decoration) precisely because text-decoration can only
           draw on one edge — the design calls for a line top and bottom. The
           link already carries py-2, which spaces the rules off the text;
           the header row is fixed-height + items-center, so the extra 2px
           edges don't shift the row. */
        .header-bar nav a.nav-active {
          border-top: 2px solid ${navLineColor};
          border-bottom: 2px solid ${navLineColor};
        }
        /* Same double-line (top + bottom secondary-color rules) on hover of a
           main nav link, so hovering previews the active-page treatment.
           Scoped to top-level nav links; dropdown subpages use a darker-row
           hover instead (see .nav-more-dropdown a:hover above). */
        .header-bar nav a:hover {
          border-top: 2px solid ${navLineColor};
          border-bottom: 2px solid ${navLineColor};
        }
        .header-bar nav .nav-more-dropdown a:hover {
          border-top: none;
          border-bottom: none;
        }
        /* Outlined CTA buttons inside the dark drawer fill white on hover
           with dark text for a clean inversion. */
        .mobile-menu-panel .mobile-drawer-btn {
          transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .mobile-menu-panel .mobile-drawer-btn:hover {
          background-color: #fff;
          color: #101010;
          border-color: #fff;
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row — solid brand bar above the main nav row.           */
        /* ----------------------------------------------------------------- */
        /* Painted in property_color_4 (via --header-util-bg) when set, else
           --vibe-color-primary, in every header state so the strip stays
           constant while the nav row below transitions on scroll. Links use
           the luminance-picked --header-util-text, else text-on-primary. */
        .header-util-row {
          background-color: var(--header-util-bg, var(--vibe-color-primary));
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: color-mix(in srgb, var(--header-util-text, var(--vibe-color-text-on-primary)) 88%, transparent);
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        /* On the solid primary bar the links simply brighten to full
           text-on-primary on hover — no underline affordance. */
        .header-util-link:hover {
          color: var(--header-util-text, var(--vibe-color-text-on-primary));
        }

        /* ================================================================ */
        /* Header navigation typeface — Space Mono (Google Fonts)          */
        /* ----------------------------------------------------------------- */
        /* The Aster mockup's nav uses Pitch Sans (a commercial Klim font),
           but per request we use the closest Google Fonts match, Space Mono,
           loaded via the css2 link in the effect above. Applied to the main
           nav links, the phone number, Apply Now, and Resident Access in the
           top utility bar (plus the matching mobile drawer nav links /
           utility pills / phone). The hidden measurement row
           (.header-measure-row) is included so the desktop "More" overflow
           calculation measures link widths in the SAME font that's actually
           painted. The selector list sits after .header-util-link above so
           it overrides that rule's font-family: var(--vibe-font-body). */
        .header-bar nav a,
        .header-bar nav button,
        .header-measure-row span,
        .header-util-link,
        .mobile-menu-panel .mobile-drawer-btn {
          font-family: var(--vibe-font-body);
          /* Space Mono ships only 400 + 700. The links carry Tailwind
             font-semibold (600), which has no Space Mono cut, so the browser
             rounds UP to 700 (Bold). Pin to 400 (Regular) for the lighter
             weight. */
          font-weight: 400;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultValue: '280 Main St, Lucan, ON N0M 2J0, Canada',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
